import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb, Col, Container, Row } from 'react-bootstrap';
import type { IconType } from 'react-icons';
import {
  LuBuilding2,
  LuCalendar,
  LuCalendarCheck,
  LuCheck,
  LuCircleCheck,
  LuFileText,
  LuHandshake,
  LuSearch,
  LuTrendingUp,
  LuUsers,
} from 'react-icons/lu';

import GradientCTA from '../app/components/GradientCTA';
import HeroAnimated from '../app/components/HeroAnimated';
import RevealSection from '../app/components/RevealSection';
import SbrIcon from '../app/components/SbrIcon';
import { SBR_IMPACT_STATS } from '../data/sbrImpactStats';

const PEOPLE_IMG = `${process.env.PUBLIC_URL}/images/PeopleImage.jpg`;

const heroFeatures = [
  {
    title: 'Performance-Based',
    body: 'No upfront costs. You pay only when you hire.',
    icon: 'bi-bullseye',
  },
  {
    title: 'Access Top Talent',
    body: 'Tap into our network of highly qualified professionals.',
    icon: 'bi-people',
  },
  {
    title: 'Lower Risk',
    body: 'Try before you buy with no financial commitment.',
    icon: 'bi-shield-check',
  },
];

type ProcessColor = 'teal' | 'blue' | 'purple';

const processSteps: {
  n: string;
  title: string;
  body: string;
  Icon: IconType;
  color: ProcessColor;
}[] = [
  {
    n: '1',
    title: 'Define the Need',
    body: 'We align on the role, must-haves, and success criteria.',
    Icon: LuFileText,
    color: 'teal',
  },
  {
    n: '2',
    title: 'Source & Screen',
    body: 'We actively source and screen top talent.',
    Icon: LuSearch,
    color: 'blue',
  },
  {
    n: '3',
    title: 'Present Top Candidates',
    body: 'We present a curated slate of qualified, interested candidates.',
    Icon: LuUsers,
    color: 'purple',
  },
  {
    n: '4',
    title: 'Interview & Select',
    body: 'You interview and select the right fit for your team.',
    Icon: LuCircleCheck,
    color: 'teal',
  },
  {
    n: '5',
    title: 'Hire & Pay',
    body: 'You hire the candidate. We earn our success fee.',
    Icon: LuHandshake,
    color: 'blue',
  },
];

const whyChooseItems = [
  'No upfront fees or hidden costs',
  'Pay only when you hire the right candidate',
  'Access to an extensive network of passive and active talent',
  'Focused, efficient, and tailored to your needs',
  'Quality-driven approach with speed and flexibility',
];

const idealForItems: { title: string; body: string; Icon: IconType }[] = [
  {
    title: 'Specialized & Hard-to-Fill Roles',
    body: 'Niche expertise and roles that are difficult to source through traditional channels.',
    Icon: LuBuilding2,
  },
  {
    title: 'Growth & Scale',
    body: 'Rapid hiring to support expansion without compromising on quality.',
    Icon: LuTrendingUp,
  },
  {
    title: 'Project-Based Hiring',
    body: 'Time-bound engagements with clear scope, timelines, and deliverables.',
    Icon: LuCalendarCheck,
  },
  {
    title: 'Standard & Non-Executive Roles',
    body: 'High-volume professional hires outside the executive suite.',
    Icon: LuUsers,
  },
];

type ResultsVariant = 'teal' | 'blue';

const resultsStats: {
  value: string;
  label: string;
  Icon: IconType;
  variant: ResultsVariant;
}[] = SBR_IMPACT_STATS.map((stat, index) => ({
  ...stat,
  variant: index % 2 === 0 ? 'teal' : 'blue',
}));

export default function ContingentSearchPage() {
  return (
    <>
      <section className="hero-pattern text-white">
        <Container className="py-5 py-lg-6">
          <Breadcrumb className="solution-hero-breadcrumb mb-4">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
              Solutions
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Contingent Search</Breadcrumb.Item>
          </Breadcrumb>

          <Row className="align-items-stretch gy-4">
            <Col lg={6} className="d-flex flex-column">
              <HeroAnimated>
                <h1 data-hero-item className="display-5 fw-bold mb-3">
                  Contingent Search.
                  <span className="d-block text-gradient-teal-blue mt-2">High-quality talent.</span>
                  <span className="d-block text-primary mt-2">No upfront fees.</span>
                </h1>
                <p data-hero-item className="lead text-white-50 mb-4 pe-lg-2">
                  Our contingent search model delivers the right talent, when you need it. You pay only when we
                  deliver the candidate you hire.
                </p>
                <Row data-hero-item className="g-3 mt-auto">
                  {heroFeatures.map((f) => (
                    <Col sm={4} key={f.title}>
                      <div className="solution-hero-feature">
                        <div className="solution-hero-feature__icon">
                          <i className={`bi ${f.icon}`} aria-hidden />
                        </div>
                        <div className="solution-hero-feature__copy">
                          <div className="solution-hero-feature__title">{f.title}</div>
                          <p className="solution-hero-feature__body">{f.body}</p>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </HeroAnimated>
            </Col>
            <Col lg={6}>
              <RevealSection className="h-100">
                <div className="solution-hero-media h-100">
                  <img
                    src={PEOPLE_IMG}
                    alt="Professionals collaborating around a laptop in a modern office"
                    className="solution-hero-media__img"
                    width={800}
                    height={600}
                    decoding="async"
                  />
                  <div className="solution-hero-media__stat">
                    <i className="bi bi-people solution-hero-media__stat-icon" aria-hidden />
                    <div className="solution-hero-media__stat-copy">
                      <div className="solution-hero-media__stat-value">95%+</div>
                      <div className="solution-hero-media__stat-label">Technical/Niche roles filled</div>
                    </div>
                  </div>
                </div>
              </RevealSection>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="bg-white py-5 py-lg-6">
        <RevealSection>
          <Container>
            <div className="sbr-section-header cs-process__header text-center mb-5">
              <h2 className="sbr-section__title fw-bold mb-3">Our Contingent Search Process</h2>
              <p className="sbr-section__lead cs-process__subtitle mb-0 mx-auto">
                A streamlined process designed to deliver quality talent—fast.
              </p>
            </div>

            <div className="cs-process__track">
              {processSteps.map((s, index) => (
                <Fragment key={s.n}>
                  <article className={`cs-process-step cs-process-step--${s.color}`}>
                    <div className="cs-process-step__card">
                      <div className="cs-process-step__icon" aria-hidden>
                        <SbrIcon icon={s.Icon} size={26} />
                      </div>
                      <div className="cs-process-step__num">{s.n}</div>
                      <h3 className="cs-process-step__heading">{s.title}</h3>
                      <p className="cs-process-step__body">{s.body}</p>
                    </div>
                  </article>
                  {index < processSteps.length - 1 && <div className="cs-process__connector" aria-hidden />}
                </Fragment>
              ))}
            </div>
          </Container>
        </RevealSection>
      </section>

      <section className="bg-sbr-light py-5 py-lg-6">
        <RevealSection>
          <Container>
            <Row className="gy-4 align-items-stretch cs-benefits-row">
              <Col lg={6} className="d-flex">
                <div className="cs-why-choose w-100">
                  <h2 className="cs-why-choose__title sbr-card__title mb-4">
                    Why Choose Our Contingent Search?
                  </h2>
                  <ul className="cs-why-choose__list list-unstyled mb-0">
                    {whyChooseItems.map((t) => (
                      <li key={t} className="cs-why-choose__item">
                        <span className="cs-why-choose__check" aria-hidden>
                          <SbrIcon icon={LuCheck} size={14} />
                        </span>
                        <span className="cs-why-choose__text">{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
              <Col lg={6} className="d-flex">
                <div className="cs-ideal-for w-100">
                  <h2 className="cs-ideal-for__title sbr-card__title mb-4">Ideal For</h2>
                  <div className="cs-ideal-for__list">
                    {idealForItems.map((x) => (
                      <div key={x.title} className="cs-ideal-for__row">
                        <div className="cs-ideal-for__icon" aria-hidden>
                          <SbrIcon icon={x.Icon} size={22} />
                        </div>
                        <div className="cs-ideal-for__copy">
                          <div className="cs-ideal-for__heading">{x.title}</div>
                          <p className="cs-ideal-for__body mb-0">{x.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </RevealSection>
      </section>

      <section className="bg-white py-5 py-lg-6 border-top border-secondary border-opacity-25">
        <RevealSection>
          <Container>
            <div className="sbr-section-header text-center mb-5">
              <h2 className="sbr-section__title fw-bold mb-0">Results That Speak for Themselves</h2>
            </div>
            <div className="cs-results">
              {resultsStats.map((m, index) => (
                <Fragment key={m.value}>
                  {index > 0 && <div className="cs-results__divider" aria-hidden />}
                  <div className={`cs-results__item cs-results__item--${m.variant}`}>
                    <div className="cs-results__icon" aria-hidden>
                      <SbrIcon icon={m.Icon} size={26} />
                    </div>
                    <div className="cs-results__copy">
                      <div className="cs-results__value">{m.value}</div>
                      <p className="cs-results__label mb-0">{m.label}</p>
                    </div>
                  </div>
                </Fragment>
              ))}
            </div>
          </Container>
        </RevealSection>
      </section>

      <GradientCTA
        icon={<SbrIcon icon={LuCalendar} size={26} />}
        title={
          <>
            <div>Need top talent without the upfront risk?</div>
            <div className="sbr-cta__subtitle fw-normal opacity-90 mt-1 mb-0">
              Let&apos;s find the right person for the role.
            </div>
          </>
        }
      />
    </>
  );
}
